<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import TimeRangeFields from '../components/TimeRangeFields.vue';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { reportInvalidForm, scrollToFirstError } from '../formFeedback';
import { apiErrorMessage } from '../../../../api/realEstate/errors';
import {
  createAssignment,
  patchAssignment,
} from '../../../../api/realEstate/assignments';

function stripIds(slots) {
  return slots.map(s => ({ start_time: s.start_time, end_time: s.end_time }));
}

// Layer 1 — one-off assignments (docs/real-estate/be/10-api-reference.md
// §A10). PATCH only accepts start_date/end_date/time_slots — the agent
// can't be changed once an assignment exists, so edit mode shows the agent
// as read-only instead of a select.
//
// `project` is optional now (added for the Agent Calendar's drag-select
// chooser — see the Phase 3 plan): every other caller (ProjectDetail's
// AssignmentsSection) still always passes one, and edit mode always has a
// real project fetched by the caller. Only "create, opened with no single
// project" needs the project picker below; `presetAgentId`/
// `presetStartDate`/`presetEndDate` seed the form from the calendar's
// drag-selection.
export default {
  name: 'AssignmentModal',
  components: { TimeRangeFields },
  props: {
    project: {
      type: Object,
      default: null,
    },
    assignment: {
      type: Object,
      default: null,
    },
    presetAgentId: {
      type: String,
      default: '',
    },
    presetStartDate: {
      type: String,
      default: '',
    },
    presetEndDate: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      selectedProjectId: '',
      isLoadingProjects: false,
      form: {
        agent_id: this.assignment
          ? this.assignment.agent_id
          : this.presetAgentId,
        start_date: this.assignment
          ? this.assignment.start_date
          : this.presetStartDate,
        end_date: this.assignment
          ? this.assignment.end_date
          : this.presetEndDate,
      },
      timeSlots: this.assignment
        ? this.assignment.time_slots.map(s => ({
            start_time: s.start_time,
            end_time: s.end_time,
          }))
        : [{ start_time: '', end_time: '' }],
      isSubmitting: false,
      submitted: false,
    };
  },
  computed: {
    ...mapGetters('realEstate', { projects: 'getProjectOptions' }),
    ...mapGetters('realEstateAgents', { allAgents: 'getAgents' }),
    isEdit() {
      return !!this.assignment;
    },
    // No single project was passed in (the calendar's chooser, not an
    // existing project's Assignments section) — show a project picker
    // instead of assuming `project.property_id`. Only applies to create;
    // edit mode always has a real project fetched by the caller first.
    needsProjectPicker() {
      return !this.isEdit && !this.project;
    },
    agentOptions() {
      return this.project ? this.project.agents : this.allAgents;
    },
    agentName() {
      if (!this.isEdit || !this.project) return '';
      const agent = this.project.agents.find(
        a => a.agent_id === this.assignment.agent_id
      );
      return agent ? agent.name : this.assignment.agent_id;
    },
    fieldErrors() {
      return {
        agent_id: !this.isEdit && !this.form.agent_id,
        start_date: !this.form.start_date,
        end_date: !this.form.end_date,
        project_id: this.needsProjectPicker && !this.selectedProjectId,
      };
    },
    isDateRangeInvalid() {
      return (
        this.form.start_date &&
        this.form.end_date &&
        this.form.end_date < this.form.start_date
      );
    },
    isFormValid() {
      return (
        !Object.values(this.fieldErrors).some(Boolean) &&
        !this.isDateRangeInvalid &&
        !this.hasInvalidSlot
      );
    },
    hasInvalidSlot() {
      return this.timeSlots.some(
        slot =>
          (slot.start_time && !slot.end_time) ||
          (!slot.start_time && slot.end_time) ||
          (slot.start_time && slot.end_time && slot.end_time <= slot.start_time)
      );
    },
    filledTimeSlots() {
      return this.timeSlots.filter(s => s.start_time && s.end_time);
    },
  },
  mounted() {
    if (this.needsProjectPicker) {
      if (!this.allAgents.length) this.fetchAgents();
      this.loadProjects();
    }
  },
  methods: {
    ...mapActions('realEstateAgents', ['fetchAgents']),
    ...mapActions('realEstate', ['fetchProjectOptions']),
    // Always refreshes (a project may have been added or renamed since the
    // list was cached); the spinner only shows while there's nothing yet.
    async loadProjects() {
      this.isLoadingProjects = !this.projects.length;
      try {
        await this.fetchProjectOptions();
      } finally {
        this.isLoadingProjects = false;
      }
    },
    handleSlotChange(index, value) {
      this.timeSlots.splice(index, 1, value);
    },
    addSlot() {
      this.timeSlots.push({ start_time: '', end_time: '' });
    },
    removeSlot(index) {
      this.timeSlots.splice(index, 1);
    },
    async handleSubmit() {
      this.submitted = true;
      if (this.isDateRangeInvalid) {
        useAlert(this.$t('REAL_ESTATE.ERRORS.INVALID_DATE_RANGE'));
        scrollToFirstError(this);
        return;
      }
      if (this.hasInvalidSlot) {
        useAlert(this.$t('REAL_ESTATE.ERRORS.INVALID_TIME_RANGE'));
        scrollToFirstError(this);
        return;
      }
      if (!this.isFormValid) {
        reportInvalidForm(this);
        return;
      }
      this.isSubmitting = true;
      try {
        if (this.isEdit) {
          await this.saveEdit();
          useAlert(this.$t('REAL_ESTATE.ASSIGNMENTS.SUCCESS_UPDATED'));
        } else {
          await createAssignment({
            property_id: this.project
              ? this.project.property_id
              : this.selectedProjectId,
            agent_id: this.form.agent_id,
            start_date: this.form.start_date,
            end_date: this.form.end_date,
            time_slots: this.filledTimeSlots,
          });
          useAlert(this.$t('REAL_ESTATE.ASSIGNMENTS.SUCCESS_ADDED'));
        }
        this.$emit('saved');
      } catch (err) {
        useAlert(
          apiErrorMessage(
            this,
            err,
            this.isEdit
              ? 'REAL_ESTATE.ASSIGNMENTS.ERROR_UPDATE'
              : 'REAL_ESTATE.ASSIGNMENTS.ERROR_ADD'
          )
        );
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isSubmitting = false;
      }
    },
    // Only PATCHes what changed — sending time_slots replaces all of them.
    async saveEdit() {
      const fields = {};
      if (this.form.start_date !== this.assignment.start_date) {
        fields.start_date = this.form.start_date;
      }
      if (this.form.end_date !== this.assignment.end_date) {
        fields.end_date = this.form.end_date;
      }
      const original = stripIds(this.assignment.time_slots);
      const current = stripIds(this.filledTimeSlots);
      if (JSON.stringify(original) !== JSON.stringify(current)) {
        fields.time_slots = current;
      }
      if (Object.keys(fields).length) {
        await patchAssignment(this.assignment.assignment_id, fields);
      }
    },
  },
};
</script>

<template>
  <div
    class="fixed inset-0 flex items-center justify-center bg-slate-800/80 z-50"
  >
    <div
      class="bg-white dark:bg-slate-900 rounded-lg p-6 shadow-lg w-full max-w-lg max-h-[85vh] overflow-y-auto"
    >
      <div class="flex items-center mb-4 justify-between">
        <h1 class="text-xl font-bold text-slate-900 dark:text-slate-100">
          {{
            isEdit
              ? $t('REAL_ESTATE.ASSIGNMENTS.MODAL_EDIT_TITLE')
              : $t('REAL_ESTATE.ASSIGNMENTS.MODAL_ADD_TITLE')
          }}
        </h1>
        <woot-button
          variant="clear"
          color-scheme="alert"
          icon="dismiss"
          @click="$emit('close')"
        />
      </div>

      <form class="space-y-4" novalidate @submit.prevent="handleSubmit">
        <div v-if="needsProjectPicker">
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{ $t('REAL_ESTATE.ASSIGNMENTS.FIELD_PROJECT') }}
            <span class="text-red-500">*</span>
          </label>
          <select
            v-model="selectedProjectId"
            :class="[
              selectClass,
              {
                '!border-red-500': submitted && fieldErrors.project_id,
              },
            ]"
          >
            <option value="" disabled>
              {{ $t('REAL_ESTATE.ASSIGNMENTS.FIELD_PROJECT_PLACEHOLDER') }}
            </option>
            <option
              v-for="proj in projects"
              :key="proj.property_id"
              :value="proj.property_id"
            >
              {{ proj.name }}
            </option>
          </select>
          <p
            v-if="submitted && fieldErrors.project_id"
            class="mt-1 mb-0 text-xs text-red-500"
          >
            {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
          </p>
        </div>

        <div>
          <label class="block mb-1 text-sm text-slate-700 dark:text-slate-200">
            {{
              isEdit
                ? $t('REAL_ESTATE.ASSIGNMENTS.FIELD_AGENT_LOCKED')
                : $t('REAL_ESTATE.ASSIGNMENTS.FIELD_AGENT')
            }}
            <span v-if="!isEdit" class="text-red-500">*</span>
          </label>
          <template v-if="isEdit">
            <p class="mb-0 text-sm text-slate-900 dark:text-slate-100 py-2">
              {{ agentName }}
            </p>
            <p class="mb-0 text-xs text-slate-400">
              {{ $t('REAL_ESTATE.ASSIGNMENTS.AGENT_LOCKED_HINT') }}
            </p>
          </template>
          <template v-else>
            <select
              v-model="form.agent_id"
              :class="[
                selectClass,
                {
                  '!border-red-500': submitted && fieldErrors.agent_id,
                },
              ]"
            >
              <option value="" disabled>
                {{ $t('REAL_ESTATE.ASSIGNMENTS.FIELD_AGENT_PLACEHOLDER') }}
              </option>
              <option
                v-for="agent in agentOptions"
                :key="agent.agent_id"
                :value="agent.agent_id"
              >
                {{ agent.name }}
              </option>
            </select>
            <p
              v-if="submitted && fieldErrors.agent_id"
              class="mt-1 mb-0 text-xs text-red-500"
            >
              {{ $t('REAL_ESTATE.FORM.REQUIRED') }}
            </p>
          </template>
        </div>

        <div class="grid grid-cols-2 gap-3">
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.ASSIGNMENTS.FIELD_START_DATE') }}
              <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.start_date"
              type="date"
              class="reset-base mb-0"
              :class="[
                fieldClass,
                {
                  '!border-red-500':
                    submitted && (fieldErrors.start_date || isDateRangeInvalid),
                },
              ]"
              required
            />
          </div>
          <div>
            <label
              class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
            >
              {{ $t('REAL_ESTATE.ASSIGNMENTS.FIELD_END_DATE') }}
              <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.end_date"
              type="date"
              class="reset-base mb-0"
              :class="[
                fieldClass,
                {
                  '!border-red-500':
                    submitted && (fieldErrors.end_date || isDateRangeInvalid),
                },
              ]"
              required
            />
          </div>
          <p
            v-if="submitted && isDateRangeInvalid"
            class="mt-0 mb-0 text-xs text-red-500 col-span-2"
          >
            {{ $t('REAL_ESTATE.ERRORS.INVALID_DATE_RANGE') }}
          </p>
        </div>

        <div>
          <div class="flex items-center justify-between mb-1">
            <label class="mb-0 text-sm text-slate-700 dark:text-slate-200">
              {{ $t('REAL_ESTATE.ASSIGNMENTS.FIELD_TIME_SLOTS') }}
            </label>
            <woot-button
              type="button"
              variant="clear"
              size="small"
              icon="add"
              @click="addSlot"
            >
              {{ $t('REAL_ESTATE.ASSIGNMENTS.ADD_SLOT_BUTTON') }}
            </woot-button>
          </div>
          <div
            v-for="(slot, index) in timeSlots"
            :key="index"
            class="flex items-center gap-2 mb-2"
          >
            <TimeRangeFields
              :value="slot"
              :show-all-day="false"
              @input="value => handleSlotChange(index, value)"
            />
            <woot-button
              type="button"
              variant="clear"
              size="small"
              color-scheme="alert"
              icon="delete"
              @click="removeSlot(index)"
            />
          </div>
        </div>

        <div
          class="flex justify-end gap-3 pt-4 mt-2 border-t border-slate-200 dark:border-slate-700"
        >
          <woot-button variant="clear" type="button" @click="$emit('close')">
            {{ $t('REAL_ESTATE.ASSIGNMENTS.CANCEL') }}
          </woot-button>
          <woot-button type="submit" :is-loading="isSubmitting">
            {{ $t('REAL_ESTATE.ASSIGNMENTS.SAVE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </div>
</template>
